import { syncPendingDrafts } from '@services';
import { message } from 'antd';
import { useEffect } from 'react';

/** Slow on purpose: the events below do the real work, this only catches what they miss. */
const HEARTBEAT_MS = 60000;

/**
 * Sends inspection work left on this device, wherever the user happens to be in the app.
 *
 * Mounted once, in the signed-in shell, rather than in the inspection screen. The screen is exactly
 * where it cannot live: someone fills a checklist in with no signal, closes it and gets on with the
 * day, and nothing would ever go up because sending depended on them opening that inspection again.
 *
 * Three triggers. The `online` event covers a connection returning; becoming visible again covers a
 * laptop opened at the desk, where the network was never reported as down because the machine was
 * asleep; and the heartbeat covers a server that was down, which the browser never announces.
 */
export const useInspectionSync = () => {
  useEffect(() => {
    let cancelled = false;

    const run = () => {
      void syncPendingDrafts().then(({ answers, photos }) => {
        if (cancelled || answers + photos === 0) return;

        message.success('Inspection work saved on this device has been sent.');
      });
    };

    run();

    const onVisible = () => document.visibilityState === 'visible' && run();

    window.addEventListener('online', run);
    document.addEventListener('visibilitychange', onVisible);
    const heartbeat = setInterval(run, HEARTBEAT_MS);

    return () => {
      cancelled = true;
      window.removeEventListener('online', run);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(heartbeat);
    };
  }, []);
};
